/**
 * Pure easing functions for text-overlay motion.
 *
 * These are plain math (no Remotion hooks, no DOM) so the exact same curves
 * drive the GPU/Remotion export, the in-app Player, and the editor canvas —
 * the three places overlays are drawn. Keeping the curves here is what stops
 * those renderers from drifting apart again.
 *
 * Cinematic motion = slow, weighted, decelerating. Entrances ride easeOutExpo
 * / easeOutQuint (fast arrival, long graceful settle); exits ride easeInCubic
 * (linger, then leave). easeOutBack adds a restrained overshoot for "pop".
 */

export const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x)

/** Linear interpolation. */
export const mix = (a: number, b: number, t: number): number => a + (b - a) * t

// ── Decelerating (entrances) ────────────────────────────────────────────────
export const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3)
export const easeOutQuint = (t: number): number => 1 - Math.pow(1 - t, 5)
export const easeOutExpo = (t: number): number => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t))

/** Restrained overshoot — settles slightly past 1 then back. Good for "pop". */
export const easeOutBack = (t: number): number => {
  const c1 = 1.70158
  const c3 = c1 + 1
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
}

// ── Accelerating (exits) ─────────────────────────────────────────────────────
export const easeInCubic = (t: number): number => t * t * t
export const easeInQuint = (t: number): number => t * t * t * t * t

// ── Symmetric ────────────────────────────────────────────────────────────────
export const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2

/**
 * Critically-damped spring settle, normalized to [0,1] over its own progress.
 * A touch of life without the bouncy, amateur feel of an under-damped spring.
 */
export const springSettle = (t: number): number => {
  const x = clamp01(t)
  return 1 - Math.pow(2, -10 * x) * Math.cos((x * 10 - 0.75) * ((2 * Math.PI) / 3))
}
